import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Building2, Users, MapPin, Calendar, Star, DollarSign } from 'lucide-react';
import { ICompanyDetails } from '../../redux/company/types';
import { formatCurrencyWithAbbrevation } from '../../constants/currency';
import { RichTextViewer } from '../Common/RichTextViewer';
import { CompanyVideoThumbnails } from './CompanyVideoThumbnails';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface ICompanyOverview {
    companyDetails?: ICompanyDetails;
}

const CompanyOverview: React.FC<ICompanyOverview> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box>
            <Card
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    mb: 3,
                    boxShadow: 'none',
                }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                component="h2"
                                sx={{
                                    color: COMPANY_BRAND_ORANGE,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '0.6875rem',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    mb: 2,
                                }}>
                                Company Overview
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    mb: 3,
                                    flexWrap: 'wrap',
                                }}>
                                {companyDetails?.company.service && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Building2 size={16} color={COMPANY_BRAND_ORANGE} />
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            {companyDetails.company.service}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Users size={16} color={COMPANY_BRAND_ORANGE} />
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                        }}>
                                        {companyDetails?.company.min_employees || 0}-
                                        {companyDetails?.company.max_employees || 0} employees
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MapPin size={16} color={COMPANY_BRAND_ORANGE} />
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                        }}>
                                        {companyDetails?.company.location?.address || 'Location not specified'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Calendar size={16} color={COMPANY_BRAND_ORANGE} />
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                        }}>
                                        Founded{' '}
                                        {companyDetails?.company.date_of_formation
                                            ? new Date(companyDetails.company.date_of_formation).getFullYear()
                                            : 'N/A'}
                                    </Typography>
                                </Box>
                                {companyDetails?.company.max_revenue && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DollarSign size={16} color={COMPANY_BRAND_ORANGE} />
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            {formatCurrencyWithAbbrevation(companyDetails.company.max_revenue)} revenue
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: { xs: 'center', md: 'right' }, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                                <Star size={20} color={COMPANY_BRAND_ORANGE} fill={COMPANY_BRAND_ORANGE} />
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontSize: '1.25rem',
                                        fontWeight: 'bold',
                                    }}>
                                    {companyDetails?.company.rating.toFixed(1)}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                }}>
                                {companyDetails?.company.number_of_reviews} reviews
                            </Typography>
                        </Box>
                    </Box>

                    <RichTextViewer textStyle={{ padding: 0 }} content={companyDetails?.company?.about!} />
                </CardContent>
            </Card>

            {/* Company Videos Section */}
            <CompanyVideoThumbnails videos={companyDetails?.company?.company_videos} />
        </Box>
    );
};

export { CompanyOverview };
